import dayjs from 'dayjs'
import type { BRIEFING_GROUPS, BRIEFING_ITEM_KINDS } from '@/model/middleware-types'
import type { BriefingDismissReason, BriefingItem } from '../../hooks/useBriefingDay'

export const GROUP_LABELS: Record<(typeof BRIEFING_GROUPS)[number], string> = {
  'before-visits': 'Before the visits',
  'needs-decision': 'Needs your decision',
  'labs-docs': 'Labs & documentation',
}

type ItemKind = (typeof BRIEFING_ITEM_KINDS)[number]

export const KIND_LABELS: Record<ItemKind, string> = {
  'kate-question': 'Kate check-in',
  'kate-callback': 'Kate check-in',
  message: 'Message',
  task: 'Care task',
  result: 'Lab result',
  guidance: 'Visit Guidance',
  note: 'Progress note',
}

// The card's source button, named for the place in the chart it opens (the item's `href`).
export const OPEN_LABELS: Record<ItemKind, string> = {
  'kate-question': 'Open Clinical Summary',
  'kate-callback': 'Open Clinical Summary',
  message: 'Open messages',
  task: 'Open Clinical Summary',
  result: 'Open Clinical Summary',
  guidance: 'Open in Risks & Gaps',
  note: 'Sign note',
}

export type Urgency = 'urgent' | 'soon' | 'routine'

/**
 * Red only for what is urgent (a flagged finding, an abnormal result, an overdue task); amber for
 * what needs an answer today; gray for routine work such as an unsigned note.
 */
export function urgency(item: BriefingItem, today: string): Urgency {
  if (item.severity) return 'urgent'
  switch (item.kind) {
    case 'result':
      return item.whyNow.startsWith('Abnormal') ? 'urgent' : 'soon'
    case 'task': {
      const due = item.source.date
      if (due && due < today) return 'urgent'
      return due === today ? 'soon' : 'routine'
    }
    case 'kate-question':
    case 'kate-callback':
    case 'message':
      return 'soon'
    default:
      return 'routine'
  }
}

export const URGENCY_RANK: Record<Urgency, number> = { urgent: 0, soon: 1, routine: 2 }

export const URGENCY_STYLE: Record<Urgency, { edge: string; dot: string }> = {
  urgent: { edge: 'border-l-[#e55535]', dot: 'bg-[#e55535]' },
  soon: { edge: 'border-l-[#d97706]', dot: 'bg-[#d97706]' },
  routine: { edge: 'border-l-gray-300', dot: 'bg-gray-400' },
}

const KIND_PLURALS: Record<ItemKind, [string, string]> = {
  'kate-question': ['Kate question', 'Kate questions'],
  'kate-callback': ['callback request', 'callback requests'],
  message: ['message', 'messages'],
  task: ['care task', 'care tasks'],
  result: ['lab result', 'lab results'],
  guidance: ['Visit Guidance topic', 'Visit Guidance topics'],
  note: ['unsigned note', 'unsigned notes'],
}

/** "4 Visit Guidance topics · 3 unsigned notes" */
export function kindSummary(items: BriefingItem[]): string {
  const counts = new Map<ItemKind, number>()
  for (const item of items) counts.set(item.kind, (counts.get(item.kind) ?? 0) + 1)
  return [...counts]
    .sort((a, b) => KIND_RANK[a[0]] - KIND_RANK[b[0]])
    .map(([kind, n]) => `${n} ${KIND_PLURALS[kind][n === 1 ? 0 : 1]}`)
    .join(' · ')
}

// An in-place action marks one version of an item: a newer version (an amended report, a
// reopened task) comes back as new work.
export const doneKey = (item: BriefingItem) => `${item.id}@${item.version}`

// The in-place action a card offers, as in the product's own screens: a result or a Kate
// check-in can be marked reviewed, a care task done. `did` is what the card says afterwards.
export function quickAction(item: BriefingItem): { label: string; did: string } | null {
  switch (item.kind) {
    case 'result':
    case 'kate-question':
    case 'kate-callback':
      return { label: 'Mark reviewed', did: 'Reviewed' }
    case 'task':
      return item.taskStatus ? { label: 'Mark done', did: 'Done' } : null
    default:
      return null
  }
}

// Patient avatars without a photo: one soft background per patient, picked from the name.
const AVATAR_COLORS = [
  { bg: '#fde8dc', fg: '#9a3412' },
  { bg: '#dbe7f6', fg: '#1e3a8a' },
  { bg: '#e3f0d9', fg: '#3f6212' },
  { bg: '#ece6fb', fg: '#5b21b6' },
  { bg: '#fdf1d6', fg: '#92400e' },
  { bg: '#d9f3e6', fg: '#065f46' },
]

export function avatarColors(name: string | null): { bg: string; fg: string } {
  let hash = 0
  for (const ch of name ?? '') hash = (hash * 31 + ch.charCodeAt(0)) | 0
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

// "Most urgent first" inside a group, then by visit time.
export const KIND_RANK: Record<ItemKind, number> = {
  result: 1,
  guidance: 2,
  message: 3,
  'kate-question': 4,
  'kate-callback': 4,
  note: 5,
  task: 6,
}

const VIA_LABELS: Record<string, string> = {
  sms: 'by text',
  email: 'by email',
  link: 'as a copied link',
}

/**
 * What the visit row says about a check-in that has no answers yet, e.g. "Check-in link sent by
 * text yesterday 4:10 PM, not opened yet."; null once it has answers.
 */
export function kateStateText(
  status: string,
  sentAt: string | null = null,
  sentVia: string | null = null,
): string | null {
  const sent = ['Check-in link sent', sentVia ? VIA_LABELS[sentVia] : null, formatWhen(sentAt)]
    .filter(Boolean)
    .join(' ')
  switch (status) {
    case 'not_sent':
      return 'Check-in not sent yet.'
    case 'sent':
      return `${sent}, not opened yet.`
    case 'opened':
      return `${sent}, opened but not finished yet.`
    case 'in_progress':
      return 'The patient is doing the check-in now.'
    case 'failed':
      return "Kate's check-in call did not finish."
    default:
      return null
  }
}

const LANGUAGES: Record<string, string> = { en: 'English', es: 'Spanish' }

export function languageName(code: string | null): string | null {
  return code ? (LANGUAGES[code] ?? code) : null
}

/** "Insurance: No changes reported" → label "Insurance", value "No changes reported". */
export function splitFact(fact: string): { label: string | null; value: string } {
  const at = fact.indexOf(': ')
  return at > 0
    ? { label: fact.slice(0, at), value: fact.slice(at + 2) }
    : { label: null, value: fact }
}

/** Due-date words for a care task: "due today", "overdue since Oct 3", "due Oct 21". */
function dueWords(due: string | null, today: string): string | null {
  if (!due) return null
  if (due === today) return 'due today'
  return due < today ? `overdue since ${formatWhen(due)}` : `due ${formatWhen(due)}`
}

/**
 * The card's one plain sentence, the way a colleague would say it
 * ("Answer the patient's question: …", "Sign the Oct 5 progress note").
 */
export function actionSentence(item: BriefingItem, today: string): string {
  switch (item.kind) {
    case 'kate-question':
      return item.detail
        ? `Answer the patient's question: “${item.detail}”`
        : "Answer the patient's question"
    case 'kate-callback':
      return 'Call the patient back: they asked the care team for a call'
    case 'message':
      return `Read the care team's message: ${item.detail || item.title}`
    case 'task': {
      const due = dueWords(item.source.date, today)
      return due ? `${item.title}, ${due}` : item.title
    }
    case 'result': {
      const abnormal = item.whyNow.startsWith('Abnormal')
      return `Review the ${abnormal ? 'abnormal' : 'new'} ${item.title}${item.detail ? `: ${item.detail}` : ''}`
    }
    case 'guidance':
      return item.title
    case 'note':
      return item.source.date
        ? `Sign the ${dayjs(item.source.date).format('MMM D, h:mm A')} progress note`
        : 'Sign the progress note'
  }
}

/** When an item with no visit today happened or is due: "9:05 AM", "Due today", "Oct 3". */
export function offScheduleWhen(item: BriefingItem, today: string): string | null {
  if (item.kind === 'task') {
    const due = dueWords(item.source.date, today)
    return due ? due.charAt(0).toUpperCase() + due.slice(1) : null
  }
  return formatWhen(item.source.date).replace(/^today /, '') || null
}

export function firstName(name: string | null): string | null {
  return name?.split(' ').find(Boolean) ?? null
}

export const REASON_LABELS: Record<BriefingDismissReason, string> = {
  addressed: 'Already addressed',
  'not-relevant': 'Not relevant',
  duplicate: 'Duplicate',
}

export const SOURCE_LABELS: Record<string, string> = {
  kate: 'Kate check-ins',
  messages: 'Messages',
  tasks: 'Care Queue tasks',
  results: 'Results',
  guidance: 'Visit Guidance',
  notes: 'Progress notes',
}

export function initials(name: string | null): string {
  return (name ?? '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
}

export function formatTime(iso: string | null): string {
  return iso ? dayjs(iso).format('h:mm A') : ''
}

/** A visit time, always with AM/PM: "8:30 AM", "1:00 PM". */
export function formatClock(iso: string | null): string {
  return iso ? dayjs(iso).format('h:mm A') : ''
}

/** "today 9:12 AM", "yesterday 6:42 PM", or "Oct 3, 9:12 AM" (date-only values: "Oct 3"). */
export function formatWhen(value: string | null): string {
  if (!value) return ''
  const d = dayjs(value)
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return d.format('MMM D')
  if (d.isSame(dayjs(), 'day')) return `today ${d.format('h:mm A')}`
  if (d.isSame(dayjs().subtract(1, 'day'), 'day')) return `yesterday ${d.format('h:mm A')}`
  return d.format('MMM D, h:mm A')
}

/** A Kate check-in is often finished the day before the visit, so its date says so. */
export function sourceWhen(item: BriefingItem): string {
  const when = formatWhen(item.source.date)
  const kate = item.kind === 'kate-question' || item.kind === 'kate-callback'
  return kate && when ? `completed ${when}` : when
}

/** Why the card shows the item: "Abnormal, not reviewed by you · today 9:12 AM". */
export function whyLine(item: BriefingItem): string {
  return [item.whyNow, sourceWhen(item)].filter(Boolean).join(' · ')
}

export function hiddenLabel(state: NonNullable<BriefingItem['state']>): string {
  return state.hidden === 'dismissed'
    ? `${REASON_LABELS[state.reason]} · ${formatWhen(state.at)}`
    : `Reminder · ${formatWhen(state.remindUntil)}`
}
